import { motion } from "framer-motion";
import { Shield, Zap, Settings2, Clock, ArrowRight } from "lucide-react";
import GlowEffect from "../common/GlowEffect";
import { useAIAgentData } from "../../hooks/useAIAgentData";

// Icon mapping dictionary to resolve string names to Lucide icon components
const iconMap: Record<string, React.ComponentType<any>> = {
  Shield,
  Zap,
  Settings2,
  Clock,
};

function renderIcon(name: string, size: number, className?: string) {
  const Icon = iconMap[name];
  return Icon ? <Icon size={size} className={className} /> : null;
}

// Shared animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12 },
  }),
} as const;

export default function FinalCTA({ onDemoClick }: { onDemoClick: () => void }) {
  const { data } = useAIAgentData();
  const { titlePart1, titlePart2, description, buttonText, bullets } = data.finalCTA;

  return (
    <section className="relative px-5 sm:px-10 md:px-16 sm:py-16 py-8">
      <GlowEffect direction="right" />
      <div className="relative z-10 text-center space-y-10">
        <motion.h2
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-4xl md:text-6xl xl:text-8xl font-bold text-white leading-tight tracking-wide"
        >
          <span className="bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent drop-shadow-[0px_4px_10px_rgba(255,255,255,0.25)]">
            {titlePart1}
          </span>
          <br />
          <span className="bg-gradient-to-r from-[#1179F9] to-[#A6A0F6] bg-clip-text text-transparent">
            {titlePart2}
          </span>
        </motion.h2>

        <motion.p
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
          className="text-2xl text-[#d9d9d9] max-w-3xl mx-auto leading-relaxed"
        >
          {description}
        </motion.p>

        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
          className="flex flex-col items-center gap-4"
        >
          <button
            onClick={onDemoClick}
            className="relative inline-flex h-14 overflow-hidden rounded-xl p-[2px] focus:outline-none group"
          >
            <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#1179F9_0%,#A6A0F6_50%,#1179F9_100%)]" />
            <span className="inline-flex h-full w-full cursor-pointer items-center justify-center rounded-xl bg-[#050011] px-12 py-3 text-base font-semibold text-white backdrop-blur-3xl group-hover:bg-[#0a0020] transition-colors gap-2">
              {buttonText} <ArrowRight size={18} />
            </span>
          </button>

          <div className="flex flex-wrap justify-center gap-8 pt-4">
            {bullets.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-[#d9d9d9]">
                <span className="text-[#1179F9]">{renderIcon(item.iconName, 16)}</span>
                {item.label}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
